import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  QaiButtonComponent,
  QaiDialogComponent,
  QaiFieldComponent,
  QaiIconComponent,
  QaiMenuItemButtonComponent,
  QaiMultiSelectComponent,
  type QaiMultiSelectOption,
} from '@qai/ui';
import { ISimulationTag } from 'src/app/interfaces/self-serve/responses/isimulation';
import { TagService } from 'src/app/services/self-serve/tag.service';
import { LanguageService } from 'src/app/services/language.service';
import { SelfServeLocalStorageUtil } from 'src/app/utils/local-storage.util';

@Component({
  selector: 'edit-simulation-tags-modal',
  standalone: true,
  templateUrl: './edit-simulation-tags-modal.component.html',
  styleUrls: ['./edit-simulation-tags-modal.component.scss'],
  imports: [
    QaiDialogComponent,
    QaiButtonComponent,
    QaiFieldComponent,
    QaiMultiSelectComponent,
    QaiMenuItemButtonComponent,
    QaiIconComponent,
    TranslocoModule,
  ],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class EditSimulationTagsModalComponent implements OnChanges {
  private tagService = inject(TagService);
  private languageService = inject(LanguageService);
  private translocoService = inject(TranslocoService);

  @ViewChild('dialog') dialogRef: QaiDialogComponent | undefined;
  @ViewChild('tagsSelect') tagsSelect?: QaiMultiSelectComponent;

  @Input() open = false;
  // Only the uuid and linked tags are read, so both the simulations table row and the
  // simulation details response can be passed straight in.
  @Input() simulation: { uuid: string; tags?: ISimulationTag[] | null } | undefined;

  /** The simulation's tags were saved — the host should reload its simulations. */
  @Output() saved = new EventEmitter<void>();
  @Output() closeModal = new EventEmitter<void>();

  tagOptions: QaiMultiSelectOption[] = [];
  selectedTagUuids: string[] = [];
  isCreatingTag = false;
  isSaving = false;
  tagError?: string;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open) {
        const tags = this.simulation?.tags ?? [];
        // Seed the options with the linked tags so their chips show before the account list loads.
        this.tagOptions = tags.map((tag) => ({ value: tag.uuid, label: tag.name }));
        this.selectedTagUuids = tags.map((tag) => tag.uuid);
        this.isCreatingTag = false;
        this.isSaving = false;
        this.tagError = undefined;
        this.tagsSelect?.clearSearch();
        this.dialogRef?.showModal();
        this.loadTags();
      } else {
        this.dialogRef?.close();
      }
    }
  }

  loadTags(): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (!accountUuid) {
      return;
    }

    this.tagService.listTagsForAccount(accountUuid).subscribe({
      next: (tags) => {
        // qai-multiselect orders chips by [options], so keep the linked tags first in their original
        // order — otherwise the chips reshuffle to the account list's order once it lands. Linked tags
        // the account list doesn't return are kept too, so their chips never lose their label.
        const linkedUuids = new Set(this.tagOptions.map((option) => option.value));
        const others = tags
          .filter((tag) => !linkedUuids.has(tag.tagUuid))
          .map((tag) => ({ value: tag.tagUuid, label: tag.name }));
        this.tagOptions = [...this.tagOptions, ...others];
        // qai-multiselect rebuilds its chips from [value] — hand it a fresh array alongside the new options.
        this.selectedTagUuids = [...this.selectedTagUuids];
      },
      error: (err: any) => {
        console.error('Failed to load tags:', err);
      },
    });
  }

  onTagsChange(values: string[]): void {
    // qai-multiselect's [disabled] is visual only — chip removal still emits, so ignore it while
    // saving and hand back a fresh array to resync the chips.
    if (this.isSaving) {
      this.selectedTagUuids = [...this.selectedTagUuids];
      return;
    }
    this.selectedTagUuids = values;
    this.tagError = undefined;
    this.tagsSelect?.clearSearch();
  }

  // Tag labels are user-supplied, so casing is folded locale-aware (Turkish dotted/dotless I).
  /** True when the typed term is not an exact (case-insensitive) match for a known tag. */
  canCreateTag(term: string): boolean {
    const trimmed = term.trim();
    if (!trimmed) {
      return false;
    }
    const locale = this.languageService.getActiveLanguage();
    return !this.tagOptions.some((o) => o.label.toLocaleLowerCase(locale) === trimmed.toLocaleLowerCase(locale));
  }

  // Tags created here are never rolled back — they survive chip removal or closing the modal.
  onCreateTag(term: string): void {
    const name = term.trim();
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (!name || !accountUuid || this.isCreatingTag) {
      return;
    }

    this.isCreatingTag = true;
    this.tagError = undefined;

    this.tagService.createTag(accountUuid, name).subscribe({
      next: ({ tagUuid }) => {
        this.isCreatingTag = false;
        this.tagOptions = [{ value: tagUuid, label: name }, ...this.tagOptions];
        this.selectedTagUuids = [...this.selectedTagUuids, tagUuid];
        this.tagsSelect?.clearSearch();
      },
      error: () => {
        this.isCreatingTag = false;
        this.tagError = this.translocoService.translate('simulationBuilder.TAG_CREATE_ERROR');
        this.tagsSelect?.clearSearch();
        this.tagsSelect?.closeDropdown();
      },
    });
  }

  handleSave(): void {
    const accountUuid = SelfServeLocalStorageUtil.getSelectedSelfServeAccountUuid();
    if (this.isSaving || !accountUuid || !this.simulation) {
      return;
    }

    this.isSaving = true;
    this.tagError = undefined;

    this.tagService.updateSimulationTags(accountUuid, this.simulation.uuid, this.selectedTagUuids).subscribe({
      next: () => {
        this.isSaving = false;
        this.saved.emit();
        this.handleClose();
      },
      // Modal stays open with the selection intact.
      error: () => {
        this.isSaving = false;
        this.tagError = this.translocoService.translate('simulationBuilder.TAG_UPDATE_ERROR');
      },
    });
  }

  handleClose(): void {
    if (this.open) {
      this.open = false;
      this.dialogRef?.close();
      this.closeModal.emit();
    }
  }
}
